import { ReactElement, useEffect, useRef, useState } from "react";
import { Background } from "./Background";
import SideMenu from "./components/SideMenu/SideMenu";
import Header from "./components/Header/Header";
import { NavigationBottombar } from "./components/BottomNav/BottomNav";
import { ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import SubscriptionCheckModal from "../subscription/SubscriptionCheckModal";
import OnboardingWelcomeModal from "../modal/OnboardingWelcomeModal";
import { useNavigate, useLocation } from "react-router-dom";
import { Routes } from "@/routes/router";
import useAuthorization from "@/hooks/useAuthorization";
import useChatNotifications from "@/hooks/useChatNotifications";
import { VersionUpdateBanner } from "@/components/VersionCheck";
import ImpersonationBanner from "./components/ImpersonationBanner";

interface Props {
    children: ReactElement;
}

const Layout = ({ children }: Props) => {
    const mainRef = useRef<HTMLDivElement>(null);
    const isMobile = useSelector((state: RootState) => state.sidebar.isMobile);
    const location = useLocation();
    const isBottomNavHidden = location.pathname.includes("/screening-ai") || location.pathname === Routes.PORTAL_CHAT;
    const isChatPage = location.pathname === Routes.PORTAL_CHAT;
    const organisation = useSelector((state: RootState) => state.organisation.organisation);
    const user = useSelector((state: RootState) => state.user.user);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const navigate = useNavigate();
    const { hasRole } = useAuthorization();

    useChatNotifications();

    useEffect(() => {
        if (organisation) {
            const isStripe = organisation.paymentProvider === 'STRIPE';
            const isPaymentInactive = !organisation.paymentActive;
            const isAdmin = hasRole('ROLE_ADMIN');

            if (isStripe && isPaymentInactive && !isAdmin) {
                setShowSubscriptionModal(true);
            }
        }
    }, [organisation, hasRole]);

    useEffect(() => {
        // Check onboarding flag after auth data is loaded
        const shouldShow = localStorage.getItem('show_first_login_wizard') === '1';
        setShowOnboarding(shouldShow);
    }, [organisation]);

    const handleGetStarted = () => {
        localStorage.removeItem('show_first_login_wizard');
        setShowOnboarding(false);
        navigate(Routes.PORTAL_TASKS);
    };

    return (
        <div className="flex flex-col app-viewport">
            {/* Impersonation banner — spans full width above the sidebar and header */}
            <ImpersonationBanner />

            <div className={`relative flex bg-gray-100 flex-1 min-h-0 ${isMobile ? "p-0" : ""}`}>
                {/* Sidebar */}
                <SideMenu isMobile={isMobile} />

                {/* Main Content Area */}
                <div className={`flex-1 flex flex-col transition-all duration-300 ${isMobile ? "px-0 py-2" : ""} overflow-hidden`}>
                    {/* Header */}
                    <Header isMobile={isMobile} />

                    {/* Version update banner */}
                    <VersionUpdateBanner />

                    {/* Main Content */}
                    <main ref={mainRef} className={`flex-1 mt-0 bg-white ${isChatPage ? 'overflow-hidden' : 'overflow-y-auto'} ${isMobile && !isBottomNavHidden ? "pb-[72px]" : ""}`}>
                        <Background>
                            <div className={isChatPage ? "h-full" : (isMobile ? "px-0 py-2" : "p-2")}>{children}</div>
                        </Background>
                    </main>
                </div>

                {/* Bottom Navigation Bar (Mobile Only) */}
                {isMobile && <NavigationBottombar scrollContainerRef={mainRef} />}

                {/* Toast Notifications */}
                <ToastContainer />

                {/* Subscription Check Modal */}
                <SubscriptionCheckModal
                    isOpen={showSubscriptionModal}
                    onClose={() => setShowSubscriptionModal(false)}
                    paymentActive={organisation?.paymentActive || false}
                    paymentProvider={organisation?.paymentProvider as 'STRIPE' | 'FORTNOX' || 'STRIPE'}
                />

                {/* First-login Onboarding Modal */}
                <OnboardingWelcomeModal
                    isOpen={showOnboarding}
                    userName={user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : undefined}
                    onGetStarted={handleGetStarted}
                />
            </div>
        </div>
    );
};

export default Layout;
